import { useState } from 'react';
import { useStudioStore } from '../../store/useStudioStore';
import { Snowflake, Play, Loader2, Cpu } from 'lucide-react';

export const TrackFreezing = () => {
  const channels = useStudioStore((state) => state.channels);
  const freezeChannel = useStudioStore((state) => state.freezeChannel);
  const unfreezeChannel = useStudioStore((state) => state.unfreezeChannel);
  const isChannelFrozen = useStudioStore((state) => state.isChannelFrozen);
  
  const [freezingChannelId, setFreezingChannelId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cpuLoad] = useState(25); // Mock CPU load - would come from system monitor

  // Calculate estimated CPU savings
  const calculateCPUSavings = () => {
    let totalEffects = 0;
    let frozenEffects = 0;

    channels.forEach((channel) => {
      const effectCount = channel.effectsCount || 0;
      totalEffects += effectCount;
      if (channel.frozen) {
        frozenEffects += effectCount;
      }
    });

    // Estimate: each effect uses ~5% CPU on average
    const estimatedCPUPerEffect = 5;
    const savedCPU = frozenEffects * estimatedCPUPerEffect;
    
    return {
      savedCPU: Math.min(savedCPU, 100), // Cap at 100%
      frozenEffects,
      totalEffects,
    };
  };

  const handleFreezeToggle = async (channelId: string) => {
    setError(null);
    
    if (isChannelFrozen(channelId)) {
      // Unfreeze channel
      unfreezeChannel(channelId);
    } else {
      // Freeze channel
      setFreezingChannelId(channelId);
      try {
        await freezeChannel(channelId);
      } catch (err) {
        setError(`Failed to freeze channel: ${err instanceof Error ? err.message : 'Unknown error'}`);
      } finally {
        setFreezingChannelId(null);
      }
    }
  };

  const frozenChannelsCount = channels.filter((ch) => ch.frozen).length;
  const { savedCPU, frozenEffects, totalEffects } = calculateCPUSavings();

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold mb-1">Track Freezing</h3>
          <p className="text-sm text-zinc-400">
            Render tracks to audio to reduce CPU load
          </p>
        </div>
        <div className="text-right">
          <div className="text-sm text-zinc-400">Frozen Channels</div>
          <div className="text-2xl font-bold text-blue-400">{frozenChannelsCount}</div>
        </div>
      </div>

      {/* CPU Savings Display */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Cpu className="w-4 h-4 text-blue-400" />
            <div className="text-xs text-zinc-400">CPU Saved</div>
          </div>
          <div className="text-2xl font-bold text-blue-400">
            {savedCPU.toFixed(1)}%
          </div>
        </div>

        <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-lg p-3">
          <div className="text-xs text-zinc-400 mb-1">Current CPU</div>
          <div className="text-2xl font-bold text-zinc-300">
            {cpuLoad.toFixed(1)}%
          </div>
        </div>

        <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-lg p-3">
          <div className="text-xs text-zinc-400 mb-1">Frozen Effects</div>
          <div className="text-2xl font-bold text-zinc-300">
            {frozenEffects}/{totalEffects}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <div className="space-y-2">
        {channels.map((channel) => {
          const frozen = isChannelFrozen(channel.id);
          const freezing = freezingChannelId === channel.id;
          const effectCount = channel.effectsCount || 0;

          return (
            <div
              key={channel.id}
              className="bg-zinc-800/50 rounded-lg p-3 flex items-center justify-between hover:bg-zinc-800/70 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: frozen ? '#3b82f6' : channel.color }}
                />
                <div>
                  <div className="font-medium">{channel.name}</div>
                  <div className="text-xs text-zinc-400">
                    {frozen ? 'Frozen' : 'Active'} • {effectCount} effects
                  </div>
                </div>
              </div>

              <button
                onClick={() => handleFreezeToggle(channel.id)}
                disabled={freezing}
                className={`
                  px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2
                  ${
                    frozen
                      ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                      : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                  }
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
              >
                {freezing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Freezing...
                  </>
                ) : frozen ? (
                  <>
                    <Play className="w-4 h-4" />
                    Unfreeze
                  </>
                ) : (
                  <>
                    <Snowflake className="w-4 h-4" />
                    Freeze
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {channels.length === 0 && (
        <div className="text-center py-8 text-zinc-400">
          <p>No channels available</p>
          <p className="text-sm mt-1">Add channels to freeze them</p>
        </div>
      )}

      <div className="bg-zinc-800/30 rounded-lg p-4 space-y-3">
        <h4 className="font-medium text-sm">How Track Freezing Works</h4>
        <ul className="text-sm text-zinc-400 space-y-1">
          <li>• Renders track with all effects to an audio file</li>
          <li>• Disables original plugins to reduce CPU load</li>
          <li>• Automatically unfreezes when track is edited</li>
          <li>• Frozen audio is stored in memory</li>
        </ul>

        {savedCPU > 0 && (
          <div className="pt-3 border-t border-zinc-700/50">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-zinc-400">Estimated CPU Savings</span>
              <span className="text-green-400 font-medium">{savedCPU.toFixed(1)}%</span>
            </div>
            <div className="w-full bg-zinc-700/50 rounded-full h-2 overflow-hidden">
              <div
                className="bg-gradient-to-r from-green-500 to-blue-500 h-full transition-all duration-500"
                style={{ width: `${Math.min(savedCPU, 100)}%` }}
              />
            </div>
            <p className="text-xs text-zinc-500 mt-2">
              Based on {frozenEffects} frozen effects (~5% CPU per effect)
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
